import sessionHandling from "./notificationUtils";
import { showToast } from "./Status";

// DRF validation errors come back keyed by field ({"phone_numbers": "..."}),
// with no top-level error/detail/message. Pull out the first readable one so
// the user sees the actual reason instead of a generic failure.
const firstFieldError = (body) => {
  if (!body || typeof body !== "object") return "";
  for (const value of Object.values(body)) {
    if (typeof value === "string") return value;
    const nested = firstFieldError(value);
    if (nested) return nested;
  }
  return "";
};

export const customAPI = async (url, method, payload) => {
  const apiToken = localStorage.getItem("token");
  let statusCode = "";
  try {
    const response = await fetch(url, {
      method: method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `token ${apiToken}`,
      },
      // fetch() throws a TypeError if a GET/HEAD request carries a body, which
      // surfaced as a bogus "Couldn't connect" toast before any request was sent.
      ...(["GET", "HEAD"].includes(String(method).toUpperCase())
        ? {}
        : { body: JSON.stringify(Array.isArray(payload) ? payload : { ...payload }) }),
    });
    statusCode = response.status;
    if (response.ok) {
      const res = response.status === 204 ? null : await response.json();
      let resp = {
        ok: response.ok,
        code: statusCode,
        responseDetails: res,
      };
      return resp;
    } else if (response.status === 401) {
      console.warn('[CustomAPI] 401 received — session expired, redirecting to login');
      localStorage.clear();
      window.location.href = '/auth/login';
      return null;
    } else if (response.status === 409) {
      sessionHandling(apiToken);
    } else {
      let errorMessage = "Request failed. Please try again.";
      try {
        const res = await response.json();
        errorMessage =
          res.error ||
          res.errors?.[0]?.message ||
          res.detail ||
          res.message ||
          firstFieldError(res) ||
          errorMessage;
      } catch {
        errorMessage = `Server error (${response.status}). Please try again.`;
      }
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('[CustomAPI] request failed:', error);
    showToast(
      "error",
      statusCode === ""
        ? "Couldn't connect. Check your internet and try again."
        : error.message
    );
    let res = {
      code: statusCode,
      message: error.message,
    };
    return res;
  }
};

export const customGetAPI = async (url) => {
  const apiToken = localStorage.getItem("token");
  let statusCode = "";
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `token ${apiToken}`,
      },
    });
    statusCode = response.status;
    if (response.ok) {
      const res = await response.json();
      let resp = {
        ok: response.ok,
        code: statusCode,
        responseDetails: res,
      };
      return resp;
    } else if (response.status === 401) {
      console.warn('[CustomAPI] 401 received — session expired, redirecting to login');
      localStorage.clear();
      window.location.href = '/auth/login';
      return null;
    } else if (response.status === 409) {
      sessionHandling(apiToken);
    } else {
      let errorMessage = "Request failed. Please try again.";
      try {
        const res = await response.json();
        errorMessage =
          res.error ||
          res.errors?.[0]?.message ||
          res.detail ||
          res.message ||
          errorMessage;
      } catch {
        errorMessage = `Server error (${response.status}). Please try again.`;
      }
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('[CustomAPI] GET request failed:', error);
    showToast(
      "error",
      statusCode === ""
        ? "Couldn't connect. Check your internet and try again."
        : error.message
    );
    let res = {
      ok: false,
      code: statusCode,
      message: error.message,
    };
    return res;
  }
};

// Same as customGetAPI but without the error toast, for GETs where a failure
// is an expected state (e.g. a record a new tenant has not created yet) and
// the caller decides what to show. 401 still logs the user out.
export const customGetAPIQuiet = async (url) => {
  const apiToken = localStorage.getItem("token");
  let statusCode = "";
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `token ${apiToken}`,
      },
    });
    statusCode = response.status;
    if (response.status === 401) {
      localStorage.clear();
      window.location.href = "/auth/login";
      return null;
    }
    if (!response.ok) {
      return { ok: false, code: statusCode, message: `Server error (${statusCode})` };
    }
    return { ok: true, code: statusCode, responseDetails: await response.json() };
  } catch (error) {
    return { ok: false, code: statusCode, message: error.message };
  }
};

export const customGetAPIWithToken = async (url, api_token) => {
  let statusCode = "";
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `token ${api_token}`,
      },
    });
    statusCode = response.status;
    if (response.ok) {
      const res = await response.json();
      let resp = {
        ok: response.ok,
        code: statusCode,
        responseDetails: res,
      };
      return resp;
    } else if (response.status === 401) {
      console.warn('[CustomAPI] 401 received — session expired, redirecting to login');
      localStorage.clear();
      window.location.href = '/auth/login';
      return null;
    } else if (response.status === 409) {
      sessionHandling(api_token);
    } else {
      let errorMessage = "Request failed. Please try again.";
      try {
        const res = await response.json();
        errorMessage =
          res.error ||
          res.errors?.[0]?.message ||
          res.detail ||
          res.message ||
          errorMessage;
      } catch {
        errorMessage = `Server error (${response.status}). Please try again.`;
      }
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('[CustomAPI] GET with token failed:', error);
    showToast(
      "error",
      statusCode === ""
        ? "Couldn't connect. Check your internet and try again."
        : error.message
    );
    let res = {
      code: statusCode,
      message: error.message,
    };
    return res;
  }
};

export const customGetAPIWithOutToken = async (url) => {
  let statusCode = "";
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    statusCode = response.status;
    if (response.ok) {
      const res = await response.json();
      let resp = {
        ok: response.ok,
        code: statusCode,
        responseDetails: res,
      };
      return resp;
    } else {
      let errorMessage = "Request failed. Please try again.";
      try {
        const res = await response.json();
        errorMessage =
          res.error ||
          res.errors?.[0]?.message ||
          res.detail ||
          res.message ||
          errorMessage;
      } catch {
        errorMessage = `Server error (${response.status}). Please try again.`;
      }
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('[CustomAPI] GET without token failed:', error);
    showToast(
      "error",
      statusCode === ""
        ? "Couldn't connect. Check your internet and try again."
        : error.message
    );
    let res = {
      code: statusCode,
      message: error.message,
    };
    return res;
  }
};

export const customDeleteAPI = async (url) => {
  const apiToken = localStorage.getItem("token");
  let statusCode = "";

  try {
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `token ${apiToken}`,
      },
    });

    statusCode = response.status;

    if (response.ok) {
      let res = null;

      // Handle empty response (204 No Content)
      try {
        res = await response.json();
      } catch {
        res = null;
      }

      return {
        ok: true,
        code: statusCode,
        responseDetails: res,
      };
    } else if (response.status === 401) {
      console.warn('[CustomAPI] 401 received — session expired, redirecting to login');
      localStorage.clear();
      window.location.href = '/auth/login';
      return null;
    } else if (response.status === 409) {
      sessionHandling(apiToken);
    } else {
      let errorMessage = "Couldn't complete the request. Please try again.";
      try {
        const res = await response.json();
        errorMessage =
          res?.error ||
          res?.errors?.[0]?.message ||
          res?.detail ||
          res?.message ||
          errorMessage;
      } catch {
        errorMessage = `Server error (${response.status}). Please try again.`;
      }
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('[CustomAPI] DELETE failed:', error);
    showToast(
      "error",
      statusCode === ""
        ? "Couldn't connect. Check your internet and try again."
        : error.message
    );

    return {
      code: statusCode,
      message: error.message,
    };
  }
};
